import './city-modal.scss';

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';

import { Modal } from '@/components/Client/AutoCredit/shared/ui';
import {
  setClientCity,
  setIsCityUpdated
} from '@/components/Client/client.store';
import { saveCityInNative } from '@/components/Client/shared/webview/base-actions';
import {
  isInWebView,
  isInsideIframe
} from '@/components/Client/shared/webview/env';
import type { CityInterface } from '@/components/Client/types/dictionaries.types';
import {
  CLIENT_ROUTES,
  getFullClientRoute
} from '@/components/Shared/routes/routes';
import { gaEvent } from '@/lib/google-analytics';
import { useAppDispatch, useAppSelector } from '@/store';

interface IProps {
  isOpened: boolean;
  onClose: (isOpened: boolean) => void;
}

const CityModal: React.FC<IProps> = ({ isOpened, onClose }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { city, dicts } = useAppSelector((state) => state.client);

  const paramsCity = searchParams.get('city');
  const isCreatingRequest = location.pathname.includes(
    getFullClientRoute(CLIENT_ROUTES.autoCreditCreate)
  );

  const sessionStorageCity: string | null = useMemo(() => {
    return sessionStorage.getItem('client-city') || null;
  }, [location.pathname]);
  const requireToOpenModal =
    !city && !paramsCity && !sessionStorageCity && isCreatingRequest;

  const cities = useMemo(() => dicts.cities || [], [dicts]);

  const handleSelectCity = (newCity: CityInterface) => {
    if (!city || city.uuid !== newCity.uuid) {
      dispatch(setClientCity(newCity));
      saveCityInNative(newCity); // Сохраняем город в нативке
      if (city) dispatch(setIsCityUpdated(true));
      gaEvent('select_city', { city_name: newCity.name });

      // В webview город не пишем в URL — иначе это смена роута для нативки.
      // В iframe город сохраняем в URL
      if (!isInWebView() || isInsideIframe()) {
        const params = new URLSearchParams(searchParams);
        if (newCity.uuid !== 'all') params.set('city', newCity.uuid);
        else params.delete('city');
        setSearchParams(params, { replace: isInsideIframe() });
      }
    }
    onClose(false);
  };

  useEffect(() => {
    if (!paramsCity || paramsCity === city?.uuid) return;
    const selectedCity = cities.find((city) => city.uuid === paramsCity);
    if (selectedCity)
      dispatch(
        setClientCity({
          name: selectedCity.name,
          uuid: selectedCity.uuid
        })
      );
  }, [searchParams, cities]);

  useEffect(() => {
    if (isInWebView() && !isInsideIframe()) return;
    if (!paramsCity && sessionStorageCity && isCreatingRequest) {
      const selectedCity = cities.find(
        (city) => city.uuid === sessionStorageCity
      );
      if (selectedCity) {
        dispatch(setClientCity(selectedCity));
        const params = new URLSearchParams(searchParams);
        params.set('city', selectedCity.uuid);
        setSearchParams(params, { replace: isInsideIframe() });
      }
    }
  }, [paramsCity, sessionStorageCity, isCreatingRequest, cities]);

  useEffect(() => {
    if (cities.length === 0) return;
    // Проверяем, что город есть в списке городов из api
    if (
      city &&
      isInWebView() &&
      !cities.some((item) => item.uuid === city?.uuid)
    ) {
      dispatch(setClientCity(null));
    }
  }, [city, cities]);

  return (
    <Modal
      title={t('client.choose-city')}
      open={isOpened || requireToOpenModal}
      onClose={() => onClose(false)}
      closeIcon={isOpened ? true : null}
      mask={{ closable: isOpened }}
    >
      <div className="city-modal__cities">
        {cities.map((item) => (
          <button
            onClick={() => handleSelectCity(item)}
            className={`city-modal__button ${
              item.uuid === city?.uuid ? 'city-modal__button--active' : ''
            }`}
            key={item.uuid}
          >
            {t(`client.${item.name}`, { defaultValue: item.name })}
          </button>
        ))}
      </div>
    </Modal>
  );
};

export default CityModal;
